import {
  DEFAULT_COPILOT_PROFILE,
  COPILOT_PROFILE_BY_GRAPHIC,
  copilotProfile,
} from '../../../config/copilotChromeProfiles';

/**
 * The runtime profile editor behind Ctrl+Alt+D.
 *
 * Settling which fragments a graphic shows is a per-graphic judgement made by
 * looking at the window, so it wants the loop a developer already has for the
 * other switches: flip it, see it, move to the next shortcut. Editing
 * copilotChromeProfiles.js instead costs a save and a rebuild per decision,
 * and the decisions only make sense next to each other.
 *
 * So the choices are held here at runtime, keyed by graphic, and copied out as
 * source when the set is complete. The same object is what eventually lifts
 * into `v_config.copilot.ui`, which takes these keys unchanged.
 *
 * Only DEVIATIONS from the default are kept. A map restating the default for
 * every graphic would be unreadable as source and would freeze today's
 * defaults into every entry, so a key set back to its default disappears and a
 * graphic with nothing left to say goes with it.
 */

export const DRAFT_STORAGE_KEY = 'copilot:profileDrafts';

/** The fragments a draft may name — the profile's own keys, nothing else. */
const FRAGMENT_KEYS = Object.keys(DEFAULT_COPILOT_PROFILE);

/** A draft holding only what differs from the default, or null if nothing does. */
export const pruneDraft = (draft, defaults = DEFAULT_COPILOT_PROFILE) => {
  const kept = {};
  for (const key of FRAGMENT_KEYS) {
    if (key in (draft || {}) && draft[key] !== defaults[key]) kept[key] = draft[key];
  }
  return Object.keys(kept).length ? kept : null;
};

/** The map with one fragment set for one graphic. Returns a new map. */
export const setDraftKey = (
  drafts, graphicId, key, value,
  defaults = DEFAULT_COPILOT_PROFILE,
  committed = COPILOT_PROFILE_BY_GRAPHIC,
) => {
  if (!graphicId || !FRAGMENT_KEYS.includes(key)) return { ...drafts };

  const next = { ...drafts };
  /*
   * First touch inherits whatever the committed map already said about this
   * graphic. The draft then REPLACES that entry rather than layering on it,
   * so without the seed, flipping one switch would silently drop the graphic's
   * other opt-outs back to the default.
   */
  const base = graphicId in next
    ? next[graphicId]
    : (pruneDraft(copilotProfile(graphicId, committed), defaults) || {});

  /*
   * The entry stays even when it deviates in nothing. Dropping it meant
   * "no draft for this graphic", which the merge reads as "use the committed
   * map" - so a graphic with a committed opt-out could never be switched back
   * on: setting the key to the default looked like no change at all. An empty
   * entry says "this graphic has been decided, and it decided on the default".
   */
  next[graphicId] = pruneDraft({ ...base, [key]: value }, defaults) || {};
  return next;
};

/**
 * The profile a graphic renders with: the committed map until the graphic has
 * been touched at runtime, that draft afterwards.
 *
 * The one place this merge happens. It was computed in both the chrome and the
 * developer strip, which is how the two could disagree about the same graphic.
 */
export const resolveProfile = (drafts, graphicId, committed = COPILOT_PROFILE_BY_GRAPHIC) =>
  (graphicId && drafts && graphicId in drafts
    ? { ...DEFAULT_COPILOT_PROFILE, ...drafts[graphicId] }
    : copilotProfile(graphicId, committed));

/** Graphics that actually deviate — an entry that says nothing is not a change. */
export const deviationCount = (drafts) =>
  Object.values(drafts || {}).filter((d) => Object.keys(d || {}).length).length;

/** The map as the source line it will be pasted into. */
export const serialiseDrafts = (drafts) => {
  // An empty entry means "decided, no deviation" — which is absence here.
  const ids = Object.keys(drafts || {})
    .filter((id) => Object.keys(drafts[id] || {}).length)
    .sort();
  if (!ids.length) return 'export const COPILOT_PROFILE_BY_GRAPHIC = Object.freeze({});';

  const body = ids
    .map((id) => {
      const pairs = FRAGMENT_KEYS
        .filter((k) => k in drafts[id])
        .map((k) => `${k}: ${drafts[id][k]}`)
        .join(', ');
      return `  ${id}: { ${pairs} },`;
    })
    .join('\n');

  return `export const COPILOT_PROFILE_BY_GRAPHIC = Object.freeze({\n${body}\n});`;
};

/**
 * A pasted host config, back into a drafts map.
 *
 * The copy is one half of a round trip: localStorage is per browser, so a
 * cleared store, a second machine or a colleague all need a way back IN.
 *
 * Read, never executed. The text comes off a clipboard, and `eval` or `new
 * Function` on it would run whatever happened to be there. The shape is known
 * because `serialiseDrafts` wrote it, so a scan for `id: { key: boolean }` is
 * enough, and it accepts the raw JSON of the storage key too.
 *
 * Returns null when the text is not a config at all, so a bad paste can be
 * refused rather than silently wiping the map.
 */
export const parseHostConfig = (text) => {
  if (typeof text !== 'string' || !text.trim()) return null;

  const body = text.slice(text.indexOf('{') + 1, text.lastIndexOf('}'));
  if (text.indexOf('{') === -1 || text.lastIndexOf('}') === -1) return null;

  const out = {};
  // id: { pairs } — the id quoted or bare, as source or as JSON.
  const entry = /["']?([A-Za-z0-9_-]+)["']?\s*:\s*\{([^{}]*)\}/g;
  let match;
  let sawEntry = false;

  while ((match = entry.exec(body)) !== null) {
    sawEntry = true;
    const [, graphicId, pairs] = match;
    const draft = {};
    const pair = /["']?([A-Za-z0-9_]+)["']?\s*:\s*(true|false)\b/g;
    let p;
    while ((p = pair.exec(pairs)) !== null) {
      const [, key, value] = p;
      if (FRAGMENT_KEYS.includes(key)) draft[key] = value === 'true';
    }
    const pruned = pruneDraft(draft);
    if (pruned) out[graphicId] = pruned;
  }

  // An empty config is a legitimate thing to paste; arbitrary prose is not.
  if (!sawEntry && !/^\s*(export[^{]*)?\{\s*\}?\s*\)?\s*;?\s*$/.test(text.trim())) return null;
  return out;
};

export const readDrafts = () => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Private mode, cleared storage, or something else's value under our key.
    return {};
  }
};

export const writeDrafts = (drafts) => {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // Non-fatal: the editor still works for this session.
  }
};

/*
 * A tiny store so the strip and the chrome share one copy. The strip is a
 * descendant of where the profile is resolved, so a subscription is the way
 * round rather than threading a setter back up through the layout.
 */
let drafts = null;
const listeners = new Set();

export const getDrafts = () => {
  if (drafts === null) drafts = readDrafts();
  return drafts;
};

export const subscribeDrafts = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const publish = (next) => {
  drafts = next;
  writeDrafts(next);
  listeners.forEach((l) => l());
};

export const setDraftFragment = (graphicId, key, value) =>
  publish(setDraftKey(getDrafts(), graphicId, key, value));

export const clearDrafts = () => publish({});

/** Replace the whole map, from a pasted config. */
export const replaceDrafts = (next) => publish({ ...next });

/** Test seam: forget the cached map so a fresh read happens. */
export const resetDraftCache = () => { drafts = null; };
